// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2024 Vic Zhu <viczhu2014@gmail.com>

import { Box, Chip, Divider, Grid, Paper, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useGetList, useTranslate } from 'react-admin'
import { isVirtualDevice } from '../common/virtualDevice'
import { useDeviceMetrics } from '../hooks/useDeviceMetrics'
import { useGlucoseDisplay } from '../hooks/useGlucoseDisplay'
import { GLUCOSE_THRESHOLDS } from '../types/dashboard'
import { generateInsightsSummary, generateKeyFindings } from '../utils/keyFindings'
import { useDateFormatter } from '../utils/localeDate'
import { SHARED_METRICS_PER_PAGE } from '../utils/paginationUtils'
import { calculateTIRStats } from '../utils/tirStats'
import { KeyCGMDataInsights } from './KeyCGMDataInsights'

interface SummaryReportSectionProps {
  petId: string | number
  deviceId: string | number
  startDate?: Date
  endDate?: Date
  targetMin?: number
  targetMax?: number
  highMax?: number
}

interface MetricCardProps {
  label: string
  value: string | number
  unit?: string
  color?: string
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, unit, color }) => (
  <Box
    sx={{
      textAlign: 'center',
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        lineHeight: 1.2,
        minHeight: '2.4em',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: color || 'text.primary',
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      {unit && (
        <Typography
          component="span"
          sx={{
            ml: 0.5,
            fontWeight: 400,
            fontSize: '1rem',
            color: 'text.secondary',
          }}
        >
          {unit}
        </Typography>
      )}
    </Box>
  </Box>
)

export const SummaryReportSection: React.FC<SummaryReportSectionProps> = ({
  petId,
  deviceId,
  startDate: startDateProp,
  endDate: endDateProp,
  targetMin,
  targetMax,
  highMax,
}) => {
  const translate = useTranslate()
  const fmtDate = useDateFormatter()
  const { glucoseUnit, convertValue, formatValue } = useGlucoseDisplay()

  // S4 guard: default dates must be stable per mount, NOT re-created each
  // render — fresh Date objects change the activities/metrics query filter
  // (Unix-seconds tick over) and churn the react-query cache on re-render.
  const startDate = useMemo(
    () => startDateProp ?? new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // Default to last 14 days
    [startDateProp],
  )
  const endDate = useMemo(() => endDateProp ?? new Date(), [endDateProp])

  // Fetch pet information
  const { data: pet } = useGetList('pets', {
    filter: { id: petId },
    pagination: { page: 1, perPage: 1 },
  })

  // Fetch device information (only for non-virtual devices)
  const isVirtual = isVirtualDevice(deviceId)
  const { data: device } = useGetList(
    'devices',
    {
      filter: { id: deviceId },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: !isVirtual },
  )

  // Use the unified metrics hook that handles both single and virtual devices
  const {
    data: metrics = [],
    deviceIds,
    error: metricsError,
  } = useDeviceMetrics({
    deviceId,
    petId,
    startDate,
    endDate,
    sort: { field: 'timestamp', order: 'ASC' },
    // Shared with every other windowed panel widget so react-query serves them
    // all from ONE cache entry (P17-1). Previously this fell through to
    // calculateMetricsPagination, which produced a different perPage — and
    // therefore a different key — than its neighbours for the same rows.
    pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
  })

  // Owner-recorded event count for the period (Key Findings warns when 0);
  // only the total is needed, so fetch a single record.
  const {
    total: ownerEventTotal,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useGetList('activities', {
    // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
    meta: { params: { depth: 0 } },
    filter: {
      pet: petId,
      start_time_gte: Math.floor(startDate.getTime() / 1000),
      start_time_lte: Math.floor(endDate.getTime() / 1000),
    },
    pagination: { page: 1, perPage: 1 },
  })

  // Raw stats shared by the metric cards and the Key Clinical Findings
  const computedStats = useMemo(
    () => calculateTIRStats(metrics, startDate, endDate, targetMin, targetMax, highMax),
    [metrics, startDate, endDate, targetMin, targetMax, highMax],
  )

  // Calculate key metrics
  const stats = useMemo(() => {
    if (!computedStats) return null

    return {
      avgGlucose: formatValue(computedStats.avgGlucose),
      timeInRange: computedStats.tir.toFixed(0),
      cv: computedStats.cv.toFixed(0),
      stdDev: formatValue(computedStats.stdDev),
      effectiveDays: computedStats.effectiveDays,
      sensorCoverage: computedStats.dataCoverage.toFixed(0),
    }
  }, [computedStats, formatValue])

  const petInfo = pet?.[0]

  // Key CGM Data Insights — wait for the activities count so the
  // owner-records-missing warning is deterministic, not a loading artifact.
  const { keyFindings, insightsSummary } = useMemo(() => {
    if (!computedStats || activitiesLoading) return { keyFindings: [], insightsSummary: '' }

    const input = {
      startDate,
      endDate,
      glucoseUnit,
      targetMin: targetMin ?? GLUCOSE_THRESHOLDS.TARGET_MIN,
      targetMax: targetMax ?? GLUCOSE_THRESHOLDS.TARGET_MAX,
      // Same Very High cut the TIR capsule bins with (P13-1).
      highMax,
      tir: computedStats.tir,
      tar: computedStats.tar,
      tbr: computedStats.tbr,
      cv: computedStats.cv,
      stdDev: computedStats.stdDev,
      avgGlucose: computedStats.avgGlucose,
      dataCoverage: computedStats.dataCoverage,
      readingsCount: computedStats.total,
      lowReadingCount: computedStats.lowCount,
      veryLowReadingCount: computedStats.veryLowCount,
      // A FAILED count is unknown, not zero — `?? 0` turned a failed request
      // into the required "client recorded nothing" finding (eng-brain pass 77).
      ownerEventCount: activitiesError ? null : (ownerEventTotal ?? 0),
      peakGlucose: computedStats.peakGlucose,
      peakTimestampMs: computedStats.peakTimestampMs,
      nadirGlucose: computedStats.nadirGlucose,
      nadirTimestampMs: computedStats.nadirTimestampMs,
      activeDataDays: computedStats.effectiveDays,
      petName: petInfo?.name,
      surface: 'portal' as const,
    }

    return {
      keyFindings: generateKeyFindings(input, translate),
      insightsSummary: generateInsightsSummary(input, translate),
    }
  }, [
    computedStats,
    activitiesLoading,
    activitiesError,
    ownerEventTotal,
    startDate,
    endDate,
    glucoseUnit,
    targetMin,
    targetMax,
    highMax,
    petInfo?.name,
    translate,
  ])
  const deviceInfo = device?.[0]

  // A failed metrics fetch must not render as zero-stats — a vet reading
  // "TIR 0%" over a hidden error is worse than an explicit failure (same
  // class as the 5.1.64 'failed fetch rendered as no sensor' fix).
  if (metricsError) {
    return (
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fafafa' }}>
        <Typography color="error">{translate('pos.dashboard.error_loading_data')}</Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fafafa' }}>
      {/* Header with pet and device info */}
      {petInfo && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {petInfo.name} • {petInfo.breed} • {petInfo.species}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fmtDate(startDate, 'short')} - {fmtDate(endDate, 'short')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {isVirtual ? (
                <Chip
                  label={translate('resources.devices.devices_count', {
                    smart_count: deviceIds.length,
                  })}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ) : (
                deviceInfo && (
                  <Typography variant="caption" color="text.secondary">
                    {deviceInfo.name}
                  </Typography>
                )
              )}
              {stats && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {stats.sensorCoverage}% {translate('pos.glucose.coverage_suffix')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Key metrics */}
      {stats ? (
        <Grid container spacing={1} justifyContent="center">
          <Grid item xs={6} sm={4} md={2.4}>
            <MetricCard
              label={translate('pos.glucose.avg_glucose_short')}
              value={stats.avgGlucose}
              unit={glucoseUnit}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <MetricCard
              label={translate('pos.glucose.time_in_range')}
              value={stats.timeInRange}
              unit="%"
              color={Number(stats.timeInRange) >= 70 ? '#4caf50' : '#ff9800'}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <MetricCard
              label={translate('pos.glucose.cv_short')}
              value={stats.cv}
              unit="%"
              color={Number(stats.cv) <= 36 ? '#4caf50' : '#ff9800'}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <MetricCard
              label={translate('pos.glucose.std_dev_short')}
              value={stats.stdDev}
              unit={glucoseUnit}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <MetricCard
              label={translate('pos.glucose.active_data_days')}
              value={stats.effectiveDays}
            />
          </Grid>
        </Grid>
      ) : (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {translate('pos.glucose.no_data_available')}
        </Typography>
      )}

      {/* Key CGM Data Insights — vet review block derived from the same stats */}
      <KeyCGMDataInsights
        findings={keyFindings}
        summary={insightsSummary}
        startDate={startDate}
        endDate={endDate}
      />
    </Paper>
  )
}
